/**
 * SuperAdmin → Apariencia: el tema de la consola de cada cuenta (cliente). El tema es de
 * la cuenta y lo comparten todas sus empresas y usuarios. Desde acá se cambia a una cuenta,
 * o a todas de una vez; el administrador de cada cuenta también lo puede cambiar en
 * Configuración → Apariencia.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { Palette, Layers, Check, Loader2 } from 'lucide-react';
import SuperadminLayout from '../components/SuperadminLayout';
import { panelApi } from '../utils/api';
import { Encabezado, Panel, Boton, Cargando, Aviso, Modal } from '../components/ui';
import SelectorDeTema, { TEMAS } from '../../components/SelectorDeTema';

const nombreTema = (id) => TEMAS.find(t => t.id === id)?.nombre || '—';

export default function Apariencia() {
  const [cuentas, setCuentas] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [aviso, setAviso] = useState(null);
  const [guardando, setGuardando] = useState(null);   // `${cuentaId}:${tema}`
  const [paraTodas, setParaTodas] = useState(null);   // tema elegido para aplicar a todas
  const [aplicando, setAplicando] = useState(false);

  const cargar = useCallback(async () => {
    try {
      const r = await panelApi.aparienciaLista();
      setCuentas(r.data.cuentas || []);
    } catch (e) {
      setAviso({ tipo: 'error', texto: e.response?.data?.error || 'No se pudo leer la apariencia de las cuentas' });
    } finally {
      setCargando(false);
    }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const cambiar = async (cuenta, tema) => {
    setGuardando(`${cuenta.id}:${tema}`);
    try {
      await panelApi.cambiarAparienciaCuenta(cuenta.id, tema);
      setCuentas(cs => cs.map(c => c.id === cuenta.id ? { ...c, tema } : c));
      setAviso({ tipo: 'ok', texto: `${cuenta.nombre}: ${nombreTema(tema)}` });
    } catch (e) {
      setAviso({ tipo: 'error', texto: e.response?.data?.error || `No se pudo cambiar el tema de ${cuenta.nombre}` });
    } finally {
      setGuardando(null);
    }
  };

  const aplicarATodas = async () => {
    setAplicando(true);
    try {
      const r = await panelApi.aparienciaTodas(paraTodas);
      setAviso(r.data.fallidas?.length
        ? { tipo: 'error', texto: `${nombreTema(paraTodas)}: aplicado en ${r.data.aplicadas.length}, falló en ${r.data.fallidas.map(f => f.slug).join(', ')}` }
        : { tipo: 'ok', texto: `${nombreTema(paraTodas)} aplicado en ${r.data.aplicadas.length} cuenta(s)` });
      await cargar();
    } catch (e) {
      setAviso({ tipo: 'error', texto: e.response?.data?.error || 'No se pudo aplicar el tema a todas las cuentas' });
    } finally {
      setAplicando(false);
      setParaTodas(null);
    }
  };

  return (
    <SuperadminLayout>
      <Encabezado titulo="Apariencia" subtitulo="El tema de la consola de cada cuenta. Aplica a todas las empresas y usuarios de esa cuenta." />
      <Aviso aviso={aviso} onClose={() => setAviso(null)} />

      {cargando ? <Cargando texto="Leyendo el tema de cada cuenta…" /> : (
        <div className="space-y-6">
          <Panel titulo="Un tema para todas las cuentas" icono={Layers}>
            <p className="text-sm text-gray-600 mb-4">Elija un tema y se aplica a todas las cuentas activas. Cada administrador lo puede volver a cambiar en la suya.</p>
            <SelectorDeTema tema={null} guardando={null} onElegir={(id) => setParaTodas(id)} />
          </Panel>

          <Panel titulo={`Por cuenta · ${cuentas.length}`} icono={Palette} sinPadding>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 text-gray-600">
                  <tr>
                    <th className="text-left font-medium px-5 py-2.5">Cuenta</th>
                    <th className="text-left font-medium px-3 py-2.5">Tema en uso</th>
                    <th className="text-right font-medium px-5 py-2.5">Cambiar a</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {cuentas.map(c => (
                    <tr key={c.id}>
                      <td className="px-5 py-3">
                        <Link to={`/superadmin/cuentas/${c.id}`} className="font-medium text-gray-900 hover:underline">{c.nombre}</Link>
                        <div className="text-xs text-gray-500">{c.slug}</div>
                      </td>
                      <td className="px-3 py-3">
                        {c.error ? <span className="text-xs text-red-700">No se pudo leer: {c.error}</span>
                          : <span className="inline-flex items-center gap-1.5 text-gray-800"><Check className="h-4 w-4 text-[#4652B8]" />{nombreTema(c.tema)}</span>}
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex flex-wrap justify-end gap-2">
                          {TEMAS.map(t => {
                            const actual = c.tema === t.id;
                            const ocupado = guardando === `${c.id}:${t.id}`;
                            return (
                              <button key={t.id} type="button" disabled={actual || Boolean(guardando) || Boolean(c.error)}
                                onClick={() => cambiar(c, t.id)}
                                className={clsx('inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-semibold transition-colors',
                                  actual ? 'cursor-default' : 'border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50')}
                                style={actual ? { borderColor: t.acento, color: t.acento } : undefined}>
                                {ocupado ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : actual && <Check className="h-3.5 w-3.5" />}
                                {t.nombre}
                              </button>
                            );
                          })}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        </div>
      )}

      {paraTodas && (
        <Modal titulo="Tema para todas las cuentas" icono={Palette} onClose={() => setParaTodas(null)} ancho="max-w-lg"
          pie={<>
            <Boton onClick={() => setParaTodas(null)}>Cancelar</Boton>
            <Boton variante="primario" icono={Layers} cargando={aplicando} onClick={aplicarATodas}>Aplicar a todas</Boton>
          </>}>
          <p className="text-sm text-gray-600">Todas las cuentas activas van a usar <strong>{nombreTema(paraTodas)}</strong>.</p>
        </Modal>
      )}
    </SuperadminLayout>
  );
}
